import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FUNCTION_RESULT_STATUS } from '@nova/common-interfaces';
import { ICustomerOverview, ReferenceKeyHelper } from '@rfm/shared-interfaces';
import {
  StripeAddressElementOptions,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import {
  injectStripe,
  StripeAddressComponent,
  StripeElementsDirective,
  StripePaymentElementComponent
} from 'ngx-stripe';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { CustomerService } from '../../../../../shared/services/customers/customers.service.mjs';
import { PaymentService } from '../../../../../shared/services/financial/payment.service.mjs';
import { ThirdPartyStripeService } from '../../../../../shared/services/third-party/stripe/third-party-stripe.service';

@Component({
  selector: 'app-stripe-checkout-form',
  templateUrl: './stripe-payment.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    StripeElementsDirective,
    InputNumberModule,
    StripePaymentElementComponent,
    StripeAddressComponent,
    ToggleSwitchModule,
    ButtonModule,
  ],
})
export class ThirdpartyStripeCheckoutFormComponent implements OnInit {
  // Replace with your own public key
  stripe = injectStripe(environment.stripeApiKey);
  paying = signal(false);
  checked = true;
  stripePaymentComplete = false;
 
  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this._isLoading.asObservable();

  @ViewChild(StripePaymentElementComponent)
  paymentElement!: StripePaymentElementComponent;

  @ViewChild(StripePaymentElementComponent)
  addressElement!: StripeAddressComponent;

  @Input()
  customer_uid = signal('');

  @Input()
  payment_amount = signal(0);

  customer_overview: ICustomerOverview | undefined;

  toggleSwitchChanged(event: any)
  {
    console.log(event);
  }

  constructor(
    protected thirdPartyStripeService: ThirdPartyStripeService,
    config: DynamicDialogConfig,
    protected ref: DynamicDialogRef,
    protected customerService: CustomerService,
    protected paymentService: PaymentService
  ) {
    this.customer_uid.set(config.data['uid']);
    this.paymentElementForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required]],
      address: {
        line1: [''],
        zipcode: [''],
        city: [''],
      },
      amount: [0, [Validators.required, Validators.pattern(/d+/)]],
    });
  }

  ngOnInit(): void {
    this._isLoading.next(true);

    this.customerService
      .getOverview(this.customer_uid())
      .then(async (result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.log('ERROR GETTING CUSTOMER FOR STRIPE PAYMENT');
          return Promise.reject('ERROR GETTING CUSTOMER');
        }

        this.customer_overview = result.data!;

        this.payment_amount.set(this.customer_overview!.balances?.data.reduce((accumulator: any, current: any) => accumulator + current.current_balance, 0));

        if (!environment.production && this.payment_amount() === 0)
        {
          this.payment_amount.set(101);
        }

        await this.setupPaymentIntent(this.payment_amount());
      });
  }

  modelChanged() {
    if (this.payment_amount() > 0)
    {
    }
  }

  getButtonLabel() : string
  {
    return `Pay $ ${(this.payment_amount() as number).toFixed(2)} Now`;
  }

  async setupPaymentIntent(amount: number) {
    this.paymentElementForm.setValue({
      name: this.customer_overview?.name,
      email: this.customer_overview?.email,
      address: {
        line1: this.customer_overview?.mailing_address?.line1 ?? "",
        city: this.customer_overview?.mailing_address?.town ?? "",
        zipcode: this.customer_overview?.mailing_address?.postal_code ?? ""
      },
      amount: amount,
    });

    const stripeId = ReferenceKeyHelper.getId(
      'stripe',
      this.customer_overview!.reference_keys.data
    );

    console.log('STRIPE ID', stripeId);

    this.thirdPartyStripeService
      .createPaymentIntent(
        stripeId!,
        amount * 100, //stripe requires amount to be sent in cents
        'usd',
        this.checked
      )
      .then((result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.error('GETTING CLIENT SECRET FAILED', result);
          return Promise.reject('UNABLE TO GET CLIENT SECRET');
        }
        this.elementsOptions.clientSecret = result.data!.client_secret!;
        return Promise.resolve();
      })
      .finally(() => {
        this._isLoading.next(false);
      });
  }

  fb = inject(UntypedFormBuilder);

  paymentElementForm: FormGroup;

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
    appearance: {
      theme: 'night'
    }
  };

  paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: false,
    }
  };

  billingAddressOptions: StripeAddressElementOptions = {
    mode: 'billing'
  }

  async pay() {
    try
    {
    console.log('pay clicked', this.paymentElementForm!.invalid);
    this.paying.set(true);

    console.log('confirming payment');
    await this.stripe
      .confirmPayment({
        elements: this.paymentElement.elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: this.paymentElementForm?.get('name')?.value,
              email: this.paymentElementForm?.get('email')?.value,
              address: {
                line1: this.paymentElementForm?.get('line1')?.value,
                postal_code: this.paymentElementForm?.get('postal_code')?.value,
                city: this.paymentElementForm?.get('city')?.value,
              },
            },
          },
        },
        redirect: 'if_required',
      })
      .subscribe(async (result) => {
        if (result.error) {
          // Show error to your customer (e.g., insufficient funds)
          alert({ success: false, error: result.error.message });
        } else {
          this.stripePaymentComplete = true;
          // The payment has been processed!
          if (result.paymentIntent.status === 'succeeded' || result.paymentIntent.status === "processing") {
            await this.paymentService.addPaymentIntent(result.paymentIntent.id, this.customer_overview!);
            this.ref.close(result.paymentIntent);
            // Show a success message to your customer
          }
        }
        this.paying.set(false);
      });
    }
    catch (error)
    {
      console.error(error);
      this.paying.set(false);
      if (this.stripePaymentComplete)
      {
        this.ref.close(undefined);
      }

    }
  }

  cancel() {
    this.ref.close();
  }
}
